import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setCurrentStep,
  setActualAmount,
  addMatch,
  revertMatch,
  fetchStatementDetail,
  fetchMoreDetailRows,
  fetchExpectationsPage,
  StatementDetail,
  ExpectationRecord,
  MatchRecord,
} from "../../../redux/Commission/commissionStatementSlice";
import { formatGBP } from "../../../utils/currency";

// ─── Colors ───────────────────────────────────────────────────────────────────
const NAVY   = "#205393";
const RED    = "#c92a2a";
const T1     = "#0f172a";
const T2     = "#475569";
const T3     = "#94a3b8";
const GRN    = "#2b8a3e";
const GRNL   = "#ebf9f0";
const ORA    = "#c05621";
const AMB    = "#b45309";
const BRD    = "#e2e5ea";
const BRD2   = "#c8cdd5";
const SUR1   = "#fafbfd";
const SUR2   = "#f0f2f4";
const SEL_BG = "rgba(32,83,147,0.07)";
const MIS_BG = "rgba(180,83,9,0.06)";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtAmt = (v: string | number | null | undefined): string =>
  formatGBP(v, { blank: "—" });
const fmtDate = (d: string | null | undefined): string =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ─── Icons ────────────────────────────────────────────────────────────────────
const SplitIcon = () => (
  <div style={{ width: 22, height: 22, display: "flex", gap: 3, padding: 3, background: NAVY, borderRadius: 5, flexShrink: 0 }}>
    <div style={{ flex: 1, background: "#fff", borderRadius: 2 }} />
    <div style={{ flex: 1, background: "#fff", borderRadius: 2 }} />
  </div>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ─── View toggle ──────────────────────────────────────────────────────────────
type ViewMode = "individual" | "grouped";
const ViewToggle: React.FC<{ view: ViewMode; onChange: (v: ViewMode) => void }> = ({ view, onChange }) => (
  <div style={{ display: "flex", background: SUR2, border: `1px solid ${BRD2}`, borderRadius: 6, padding: 2, gap: 2 }}>
    {(["individual", "grouped"] as const).map((k) => (
      <button key={k} onClick={() => onChange(k)} style={{
        height: 28, padding: "0 14px", border: "none", borderRadius: 4,
        background: view === k ? "#1e293b" : "transparent",
        color: view === k ? "#fff" : T2,
        fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
      }}>
        {k === "individual" ? "Individual rows" : "Grouped by case"}
      </button>
    ))}
  </div>
);

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge: React.FC<{ type: "blue" | "green"; children: React.ReactNode }> = ({ type, children }) => (
  <span style={{
    padding: "2px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
    background: type === "blue" ? "rgba(32,83,147,0.12)" : GRNL,
    color: type === "blue" ? NAVY : GRN,
  }}>{children}</span>
);

// ─── Status chip ──────────────────────────────────────────────────────────────
const StatusChip: React.FC<{ status: "Due" | "Overdue" | "Paid" }> = ({ status }) => {
  const cfg = { Due: { bg: "rgba(32,83,147,0.12)", color: NAVY }, Overdue: { bg: "#ffe6e6", color: RED }, Paid: { bg: GRNL, color: GRN } }[status];
  return <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: cfg.bg, color: cfg.color }}>{status}</span>;
};

// ─── Table header styles ──────────────────────────────────────────────────────
const TH: React.CSSProperties = {
  background: SUR2, borderBottom: `1px solid ${BRD}`, padding: "8px 12px",
  fontSize: 10.5, fontWeight: 700, color: T3, whiteSpace: "nowrap",
  position: "sticky", top: 0, zIndex: 1, letterSpacing: "0.05em", textTransform: "uppercase", textAlign: "left",
};
const TH_R: React.CSSProperties = { ...TH, textAlign: "right" };

// ─── Virtual row window ───────────────────────────────────────────────────────
const VIRTUAL_ROW_H = 37;
const VIRTUAL_OVERSCAN = 8;
const VIRTUAL_THRESHOLD = 150;

// `active` should be false whenever the caller won't actually render
// `visibleItems`/topPad/bottomPad (e.g. the "Grouped by case" view, which
// always renders every row itself and never consults this windowing). Without
// this, scrolling drove a setState-per-animation-frame chain that re-rendered
// the whole step on every frame for zero visual benefit once row counts grew
// past the threshold (e.g. after the Step 4 pagination fix let all of a
// large statement's rows load) -- the mouse wheel/trackpad felt unresponsive
// because every frame of native scrolling was competing with a React re-render.
function useVirtualRows<T>(items: T[], active: boolean) {
  const [scrollTop, setScrollTop] = useState(0);
  const rafRef = useRef<number>(0);
  useEffect(() => () => { cancelAnimationFrame(rafRef.current); }, []);
  const isVirtualized = active && items.length > VIRTUAL_THRESHOLD;
  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!isVirtualized) return;
    const top = (e.currentTarget as HTMLDivElement).scrollTop;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setScrollTop(top));
  }, [isVirtualized]);
  if (!isVirtualized) return { onScroll, visibleItems: items, topPad: 0, bottomPad: 0 };
  const panelH = typeof window !== "undefined" ? Math.max(280, window.innerHeight - 420) : 480;
  const start = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_H) - VIRTUAL_OVERSCAN);
  const end = Math.min(items.length, Math.ceil((scrollTop + panelH) / VIRTUAL_ROW_H) + VIRTUAL_OVERSCAN);
  return { onScroll, visibleItems: items.slice(start, end), topPad: start * VIRTUAL_ROW_H, bottomPad: (items.length - end) * VIRTUAL_ROW_H };
}

// ─── Component ────────────────────────────────────────────────────────────────
const StepValidateMatch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userId = useSelector((s: RootState) => s.authUser?.userId ?? "");
  const {
    detailRows, detailHasMore, loadingMoreDetail,
    expectations, expectationHasMore, loadingMoreExpectations,
    actualAmounts, matches,
    statementId, providerId, startDate, endDate,
    loadingDetail, loading, error,
  } = useSelector((s: RootState) => s.commissionStatement);

  const [viewMode,         setViewMode]         = useState<ViewMode>("grouped");
  const [hoveredRef,       setHoveredRef]       = useState<string | null>(null);
  const [activePaymentIds, setActivePaymentIds] = useState<Set<string>>(new Set());
  const [activeExpIds,     setActiveExpIds]     = useState<Set<string>>(new Set());
  const [detailModalExp,   setDetailModalExp]   = useState<ExpectationRecord | null>(null);
  const [toasts,           setToasts]           = useState<{ id: number; msg: string }[]>([]);

  const detailPageRef      = useRef(1);
  const expectationPageRef = useRef(1);
  const leftSentinelRef    = useRef<HTMLDivElement | null>(null);
  const rightSentinelRef   = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    detailPageRef.current = 1;
    expectationPageRef.current = 1;
    setActivePaymentIds(new Set());
    setActiveExpIds(new Set());
  }, [statementId]);

  useEffect(() => { if (statementId) dispatch(fetchStatementDetail(statementId)); }, [statementId]);

  useEffect(() => {
    if (!statementId || !userId) return;
    expectationPageRef.current = 1;
    dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: 1 }));
  }, [statementId, userId]);

  useEffect(() => {
    const el = leftSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && detailHasMore && !loadingMoreDetail && statementId) {
        detailPageRef.current += 1;
        dispatch(fetchMoreDetailRows({ statementId, page: detailPageRef.current }));
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [detailHasMore, loadingMoreDetail, statementId]);

  useEffect(() => {
    const el = rightSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && expectationHasMore && !loadingMoreExpectations && statementId && userId) {
        expectationPageRef.current += 1;
        dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: expectationPageRef.current }));
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [expectationHasMore, loadingMoreExpectations, statementId, userId]);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg: string) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const matchedPaymentIds = useMemo(() => new Set(matches.map((m: MatchRecord) => m.paymentId)), [matches]);
  const matchedExpIds     = useMemo(() => new Set(matches.flatMap((m: MatchRecord) => m.expectationIds)), [matches]);
  const matchedCaseRefs   = useMemo(() => new Set(matches.map((m: MatchRecord) => m.caseRef)), [matches]);

  const activeDetailRows = useMemo(() => detailRows.filter(r => !r.Processed), [detailRows]);

  const activeCaseRefs = useMemo(() =>
    new Set(detailRows.filter(r => activePaymentIds.has(r.CommissionStatementDetailId)).map(r => r.CaseRef)),
  [detailRows, activePaymentIds]);

  const visibleExpectations = useMemo(() =>
    expectations.filter(e => e.CommissionReceiptId === null && (activeCaseRefs.has(e.CaseRef) || matchedCaseRefs.has(e.CaseRef))),
  [expectations, activeCaseRefs, matchedCaseRefs]);

  const mismatchPaymentIds = useMemo(() => {
    const result = new Set<string>();
    Array.from(activePaymentIds).forEach(paymentId => {
      const payment = detailRows.find(r => r.CommissionStatementDetailId === paymentId);
      if (!payment) return;
      const selExps = visibleExpectations.filter(e => e.CaseRef === payment.CaseRef && activeExpIds.has(e.CommissionExpectationId));
      if (selExps.length === 0) return;
      const sum = selExps.reduce((acc, e) => acc + parseFloat(actualAmounts[e.CommissionExpectationId] ?? e.ExpectedAmount ?? "0"), 0);
      if (Math.abs(sum - parseFloat(String(payment.ActualAmount ?? "0"))) >= 0.001) result.add(paymentId);
    });
    return result;
  }, [activePaymentIds, activeExpIds, detailRows, visibleExpectations, actualAmounts]);

  const mismatchExpIds = useMemo(() => {
    const result = new Set<string>();
    Array.from(mismatchPaymentIds).forEach(paymentId => {
      const payment = detailRows.find(r => r.CommissionStatementDetailId === paymentId);
      if (!payment) return;
      visibleExpectations.filter(e => e.CaseRef === payment.CaseRef && activeExpIds.has(e.CommissionExpectationId))
        .forEach(e => result.add(e.CommissionExpectationId));
    });
    return result;
  }, [mismatchPaymentIds, detailRows, visibleExpectations, activeExpIds]);

  const leftTotal = useMemo(() => {
    const ids = new Set([...Array.from(activePaymentIds), ...Array.from(matchedPaymentIds)]);
    return activeDetailRows.filter(r => ids.has(r.CommissionStatementDetailId))
      .reduce((s, r) => s + (parseFloat(r.ActualAmount || "0") || 0), 0);
  }, [activeDetailRows, activePaymentIds, matchedPaymentIds]);

  const selectedExpAmt = useMemo(() => {
    const ids = new Set([...Array.from(activeExpIds), ...Array.from(matchedExpIds)]);
    return Array.from(ids).reduce((s, id) => {
      const exp = expectations.find(e => e.CommissionExpectationId === id);
      if (!exp) return s;
      return s + (parseFloat(actualAmounts[id] ?? exp.ExpectedAmount ?? "0") || 0);
    }, 0);
  }, [activeExpIds, matchedExpIds, expectations, actualAmounts]);

  // ── Matching engine ───────────────────────────────────────────────────────
  // overrideAmounts: used by handleActualAmountChange to test the next value before Redux updates
  const evaluateMatches = useCallback((
    nextActivePaymentIds: Set<string>,
    nextActiveExpIds: Set<string>,
    overrideAmounts?: Record<string, string>,
  ) => {
    const amts = overrideAmounts ?? actualAmounts;
    const matchedCRefs = new Set(matches.map((m: MatchRecord) => m.caseRef));
    const nextActiveCRefs = new Set(detailRows.filter(r => nextActivePaymentIds.has(r.CommissionStatementDetailId)).map(r => r.CaseRef));
    const nextVisible = expectations.filter(
      e => e.CommissionReceiptId === null && (nextActiveCRefs.has(e.CaseRef) || matchedCRefs.has(e.CaseRef))
    );

    let curActive = new Set(nextActivePaymentIds);
    let curExps   = new Set(nextActiveExpIds);
    const newMatches: MatchRecord[] = [];

    for (const paymentId of Array.from(nextActivePaymentIds)) {
      const payment = detailRows.find(r => r.CommissionStatementDetailId === paymentId);
      if (!payment) continue;
      const selExps = nextVisible.filter(e => e.CaseRef === payment.CaseRef && curExps.has(e.CommissionExpectationId));
      if (selExps.length === 0) continue;
      const sum = selExps.reduce((acc, e) => acc + parseFloat(amts[e.CommissionExpectationId] ?? e.ExpectedAmount ?? "0"), 0);
      const paymentAmount = parseFloat(String(payment.ActualAmount ?? "0"));
      if (Math.abs(sum - paymentAmount) < 0.001) {
        curActive.delete(paymentId);
        selExps.forEach(e => curExps.delete(e.CommissionExpectationId));
        const m: MatchRecord = { paymentId, caseRef: payment.CaseRef, matchedAmount: sum, expectationIds: selExps.map(e => e.CommissionExpectationId) };
        newMatches.push(m);
        dispatch(addMatch(m));
        showToast(`Matched ${payment.CaseRef} — ${fmtAmt(sum.toFixed(2))} (${selExps.length} expectation${selExps.length !== 1 ? "s" : ""})`);
      }
    }

    if (newMatches.length > 0) {
      setActivePaymentIds(curActive);
      setActiveExpIds(curExps);
    }
  }, [detailRows, expectations, matches, actualAmounts, dispatch, showToast]);

  // ── Payment toggle ────────────────────────────────────────────────────────
  const togglePayment = useCallback((row: StatementDetail) => {
    const id = row.CommissionStatementDetailId;
    if (matchedPaymentIds.has(id)) return;
    const next = new Set(activePaymentIds);
    if (next.has(id)) {
      next.delete(id);
      const nextExp = new Set(activeExpIds);
      expectations.filter(e => e.CaseRef === row.CaseRef && e.CommissionReceiptId === null)
        .forEach(e => nextExp.delete(e.CommissionExpectationId));
      setActivePaymentIds(next);
      setActiveExpIds(nextExp);
    } else {
      const conflicting = detailRows.find(r => r.CaseRef === row.CaseRef && next.has(r.CommissionStatementDetailId));
      const nextExp = new Set(activeExpIds);
      if (conflicting) {
        next.delete(conflicting.CommissionStatementDetailId);
        expectations.filter(e => e.CaseRef === row.CaseRef && e.CommissionReceiptId === null)
          .forEach(e => nextExp.delete(e.CommissionExpectationId));
        showToast(`Switched to row ${row.RowNumber} for ${row.CaseRef} — only one payment active at a time`);
      }
      next.add(id);
      setActivePaymentIds(next);
      setActiveExpIds(nextExp);
      evaluateMatches(next, nextExp);
    }
  }, [activePaymentIds, activeExpIds, matchedPaymentIds, detailRows, expectations, showToast, evaluateMatches]);

  // ── Expectation toggle ────────────────────────────────────────────────────
  const toggleExpectation = useCallback((e: ExpectationRecord) => {
    if (matchedExpIds.has(e.CommissionExpectationId) || !activeCaseRefs.has(e.CaseRef)) return;
    const next = new Set(activeExpIds);
    next.has(e.CommissionExpectationId) ? next.delete(e.CommissionExpectationId) : next.add(e.CommissionExpectationId);
    setActiveExpIds(next);
    evaluateMatches(activePaymentIds, next);
  }, [activeExpIds, activePaymentIds, matchedExpIds, activeCaseRefs, evaluateMatches]);

  // ── Actual amount change ──────────────────────────────────────────────────
  const handleActualAmountChange = (id: string, val: string) => {
    dispatch(setActualAmount({ id, amount: val }));
    evaluateMatches(activePaymentIds, activeExpIds, { ...actualAmounts, [id]: val });
  };

  // ── Select all payments ───────────────────────────────────────────────────
  const selectAllPayments = useCallback(() => {
    if (activePaymentIds.size > 0) { setActivePaymentIds(new Set()); setActiveExpIds(new Set()); return; }
    const seen = new Map<string, string>();
    let skipped = 0;
    for (const row of activeDetailRows) {
      if (matchedPaymentIds.has(row.CommissionStatementDetailId)) continue;
      if (seen.has(row.CaseRef)) { skipped++; continue; }
      seen.set(row.CaseRef, row.CommissionStatementDetailId);
    }
    setActivePaymentIds(new Set(seen.values()));
    setActiveExpIds(new Set());
    if (skipped > 0) showToast(`${skipped} payment${skipped !== 1 ? "s" : ""} skipped — only one per case reference can be active at a time`);
  }, [activePaymentIds, activeDetailRows, matchedPaymentIds, showToast]);

  // ── Select all expectations ───────────────────────────────────────────────
  const selectAllExpectations = useCallback(() => {
    const enabled = visibleExpectations.filter(e => activeCaseRefs.has(e.CaseRef) && !matchedExpIds.has(e.CommissionExpectationId));
    const allSel = enabled.length > 0 && enabled.every(e => activeExpIds.has(e.CommissionExpectationId));
    const next = allSel ? new Set<string>() : new Set(enabled.map(e => e.CommissionExpectationId));
    setActiveExpIds(next);
    evaluateMatches(activePaymentIds, next);
  }, [visibleExpectations, activeCaseRefs, matchedExpIds, activeExpIds, activePaymentIds, evaluateMatches]);

  // ── Revert ────────────────────────────────────────────────────────────────
  const handleRevert = useCallback((paymentId: string, caseRef: string) => {
    dispatch(revertMatch(paymentId));
    showToast(`Reverted match for ${caseRef} — ready to re-reconcile`);
  }, [dispatch, showToast]);

  const getStatus = (e: ExpectationRecord): "Paid" | "Overdue" | "Due" => {
    if (e.CommissionReceiptId) return "Paid";
    if (e.DueDate && new Date(e.DueDate) < new Date()) return "Overdue";
    return "Due";
  };

  // ── Groups ────────────────────────────────────────────────────────────────
  const leftGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, StatementDetail[]>();
    for (const r of activeDetailRows) {
      const k = r.CaseRef || "(blank)";
      if (!map.has(k)) { map.set(k, []); order.push(k); }
      map.get(k)!.push(r);
    }
    return order.map(k => ({ caseRef: k, rows: map.get(k)! }));
  }, [activeDetailRows]);

  const rightGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, ExpectationRecord[]>();
    for (const e of visibleExpectations) {
      const k = e.CaseRef || "(blank)";
      if (!map.has(k)) { map.set(k, []); order.push(k); }
      map.get(k)!.push(e);
    }
    return order.map(k => ({ caseRef: k, exps: map.get(k)! }));
  }, [visibleExpectations]);

  const toggleLeftGroup = useCallback((caseRef: string, rows: StatementDetail[]) => {
    const anyActive = rows.some(r => activePaymentIds.has(r.CommissionStatementDetailId));
    if (anyActive) {
      const next = new Set(activePaymentIds);
      const nextExp = new Set(activeExpIds);
      rows.forEach(r => next.delete(r.CommissionStatementDetailId));
      expectations.filter(e => e.CaseRef === caseRef && e.CommissionReceiptId === null).forEach(e => nextExp.delete(e.CommissionExpectationId));
      setActivePaymentIds(next);
      setActiveExpIds(nextExp);
    } else {
      const first = rows.find(r => !matchedPaymentIds.has(r.CommissionStatementDetailId));
      if (first) togglePayment(first);
    }
  }, [activePaymentIds, activeExpIds, expectations, matchedPaymentIds, togglePayment]);

  const toggleRightGroup = useCallback((caseRef: string, exps: ExpectationRecord[]) => {
    if (!activeCaseRefs.has(caseRef)) return;
    const enabled = exps.filter(e => !matchedExpIds.has(e.CommissionExpectationId));
    const allSel = enabled.every(e => activeExpIds.has(e.CommissionExpectationId));
    const next = new Set(activeExpIds);
    if (allSel) enabled.forEach(e => next.delete(e.CommissionExpectationId));
    else enabled.forEach(e => next.add(e.CommissionExpectationId));
    setActiveExpIds(next);
    evaluateMatches(activePaymentIds, next);
  }, [activeCaseRefs, matchedExpIds, activeExpIds, activePaymentIds, evaluateMatches]);

  // ── Row styles ────────────────────────────────────────────────────────────
  const leftRowStyle = (row: StatementDetail): React.CSSProperties => {
    const id = row.CommissionStatementDetailId;
    const isMatched = matchedPaymentIds.has(id);
    const isActive = activePaymentIds.has(id);
    const isMismatch = mismatchPaymentIds.has(id);
    const isHovered = hoveredRef === row.CaseRef;
    if (isMatched) return { boxShadow: `inset 4px 0 0 ${GRN}`, background: GRNL, transition: "background 0.1s" };
    if (isMismatch) return { boxShadow: `inset 4px 0 0 ${AMB}`, background: MIS_BG, transition: "background 0.1s" };
    if (isActive || isHovered) return { boxShadow: `inset 4px 0 0 ${NAVY}`, background: SEL_BG, transition: "background 0.1s" };
    return { boxShadow: "inset 4px 0 0 transparent", transition: "background 0.1s" };
  };

  const rightRowStyle = (exp: ExpectationRecord): React.CSSProperties => {
    const id = exp.CommissionExpectationId;
    const isMatched = matchedExpIds.has(id);
    const isActive = activeExpIds.has(id);
    const isMismatch = mismatchExpIds.has(id);
    const isDimmed = !activeCaseRefs.has(exp.CaseRef) && !matchedCaseRefs.has(exp.CaseRef);
    const isHovered = hoveredRef === exp.CaseRef;
    if (isMatched) return { boxShadow: `inset 4px 0 0 ${GRN}`, background: GRNL, transition: "background 0.1s" };
    if (isMismatch) return { boxShadow: `inset 4px 0 0 ${AMB}`, background: MIS_BG, opacity: 1, transition: "background 0.1s" };
    if (isActive || isHovered) return { boxShadow: `inset 4px 0 0 ${NAVY}`, background: SEL_BG, transition: "background 0.1s" };
    if (isDimmed) return { boxShadow: "inset 4px 0 0 transparent", opacity: 0.4, transition: "opacity 0.1s" };
    return { boxShadow: "inset 4px 0 0 transparent", transition: "background 0.1s" };
  };

  const subStyle = (caseRef: string, anyActive: boolean): React.CSSProperties => {
    const active = anyActive || hoveredRef === caseRef;
    return { boxShadow: active ? `inset 4px 0 0 ${NAVY}` : "inset 4px 0 0 transparent", background: active ? "rgba(32,83,147,0.04)" : SUR2 };
  };

  const matchedChip: React.CSSProperties = { padding: "1px 7px", borderRadius: 10, fontSize: 10.5, fontWeight: 700, background: GRNL, color: GRN, border: `1px solid #aed6b8`, whiteSpace: "nowrap" };
  const revertBtn: React.CSSProperties   = { padding: "1px 8px", borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: "none", color: ORA, border: `1px solid ${ORA}`, cursor: "pointer", whiteSpace: "nowrap" };

  // ── Left row ─────────────────────────────────────────────────────────────
  const renderLeftRow = (row: StatementDetail, i: number) => {
    const id = row.CommissionStatementDetailId;
    const isMatched = matchedPaymentIds.has(id);
    const isActive = activePaymentIds.has(id);
    return (
      <tr key={`l-${id}-${i}`}
        style={{ ...leftRowStyle(row), borderBottom: `1px solid ${BRD}`, cursor: isMatched ? "default" : "pointer" }}
        onMouseEnter={() => setHoveredRef(row.CaseRef)} onMouseLeave={() => setHoveredRef(null)}
        onClick={() => !isMatched && togglePayment(row)}
      >
        <td style={{ padding: "8px 12px", width: 40 }}>
          <input type="checkbox" checked={isActive || isMatched} disabled={isMatched}
            onChange={() => !isMatched && togglePayment(row)} onClick={e => e.stopPropagation()}
            style={{ accentColor: isMatched ? GRN : NAVY, cursor: isMatched ? "default" : "pointer" }} />
        </td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2, textAlign: "right", fontFamily: "monospace" }}>{row.RowNumber}</td>
        <td style={{ padding: "8px 12px", fontWeight: 600, color: isMatched ? GRN : NAVY, fontSize: 12.5, fontFamily: "monospace" }}>
          {row.CaseRef || <em style={{ color: T3 }}>(blank)</em>}
        </td>
        <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 600, fontSize: 12.5, color: T1 }}>{fmtAmt(row.ActualAmount)}</td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2 }}>{row.ClientNameFromFile || "—"}</td>
        <td style={{ padding: "8px 12px", width: 130 }}>
          {isMatched && (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={matchedChip}>MATCHED</span>
              <button style={revertBtn} onClick={e => { e.stopPropagation(); handleRevert(id, row.CaseRef); }}>Revert</button>
            </div>
          )}
        </td>
      </tr>
    );
  };

  // ── Right row ─────────────────────────────────────────────────────────────
  const renderRightRow = (exp: ExpectationRecord) => {
    const id = exp.CommissionExpectationId;
    const isMatched = matchedExpIds.has(id);
    const isActive = activeExpIds.has(id);
    const isDimmed = !activeCaseRefs.has(exp.CaseRef) && !matchedCaseRefs.has(exp.CaseRef);
    const isMismatch = mismatchExpIds.has(id);
    const edited = actualAmounts[id] ?? exp.ExpectedAmount ?? "";
    const amtOk = !isMismatch && !isMatched && parseFloat(edited || "0").toFixed(2) === parseFloat(exp.ExpectedAmount || "0").toFixed(2);
    const inputBorder = isMatched || isDimmed ? BRD2 : isMismatch ? AMB : amtOk && isActive ? "#aed6b8" : BRD2;
    const matchOwner = isMatched ? matches.find((m: MatchRecord) => m.expectationIds.includes(id)) : null;
    return (
      <tr key={`r-${id}`}
        style={{ ...rightRowStyle(exp), borderBottom: `1px solid ${BRD}`, cursor: isMatched || isDimmed ? "default" : "pointer" }}
        onMouseEnter={() => !isDimmed && setHoveredRef(exp.CaseRef)} onMouseLeave={() => setHoveredRef(null)}
        onClick={() => !isMatched && !isDimmed && toggleExpectation(exp)}
      >
        <td style={{ padding: "8px 12px", width: 40 }}>
          <input type="checkbox" checked={isActive || isMatched} disabled={isMatched || isDimmed}
            onChange={() => !isMatched && !isDimmed && toggleExpectation(exp)} onClick={e => e.stopPropagation()}
            style={{ accentColor: isMatched ? GRN : NAVY, cursor: isMatched || isDimmed ? "default" : "pointer" }} />
        </td>
        <td style={{ padding: "8px 12px", fontWeight: 600, color: isMatched ? GRN : NAVY, fontSize: 12.5, fontFamily: "monospace" }}>{exp.CaseRef || "—"}</td>
        <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", fontSize: 12.5, color: T2 }}>{fmtAmt(exp.ExpectedAmount)}</td>
        <td style={{ padding: "8px 12px", textAlign: "right" }}>
          <input type="number" step="0.01" value={edited} placeholder="Amount"
            disabled={isMatched || isDimmed}
            onChange={e => handleActualAmountChange(id, e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{ width: 104, height: 26, padding: "0 8px", border: `1px solid ${inputBorder}`, borderRadius: 4, fontSize: 12, fontFamily: "monospace", textAlign: "right", outline: "none", background: isMatched || isDimmed ? SUR2 : "#fff" }} />
        </td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2 }}>{fmtDate(exp.DueDate)}</td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2 }}>{exp.ClientNameDb || "—"}</td>
        <td style={{ padding: "4px 12px", width: 145 }}>
          {isMatched && matchOwner ? (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={matchedChip}>MATCHED</span>
              <button style={revertBtn} onClick={e => { e.stopPropagation(); handleRevert(matchOwner.paymentId, matchOwner.caseRef); }}>Revert</button>
            </div>
          ) : !isDimmed ? (
            <button style={{ background: "none", border: "none", color: T3, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}
              title="View details" onClick={e => { e.stopPropagation(); setDetailModalExp(exp); }}>
              <EyeIcon />
            </button>
          ) : null}
        </td>
      </tr>
    );
  };

  // ── Left subtotal ─────────────────────────────────────────────────────────
  const renderLeftSub = (g: { caseRef: string; rows: StatementDetail[] }, isLast: boolean) => {
    const anyActive = g.rows.some(r => activePaymentIds.has(r.CommissionStatementDetailId) || matchedPaymentIds.has(r.CommissionStatementDetailId));
    const total = g.rows.reduce((s, r) => s + (parseFloat(r.ActualAmount || "0") || 0), 0);
    return (
      <React.Fragment key={`ls-${g.caseRef}`}>
        <tr style={{ ...subStyle(g.caseRef, anyActive), cursor: "pointer" }}
          onMouseEnter={() => setHoveredRef(g.caseRef)} onMouseLeave={() => setHoveredRef(null)}
          onClick={() => toggleLeftGroup(g.caseRef, g.rows)}>
          <td style={{ padding: "5px 12px", borderBottom: `2px solid ${BRD}` }} />
          <td style={{ padding: "5px 12px", borderBottom: `2px solid ${BRD}` }} />
          <td style={{ padding: "5px 12px", fontSize: 11.5, color: T3, fontStyle: "italic", borderBottom: `2px solid ${BRD}` }}>Subtotal</td>
          <td style={{ padding: "5px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: T1, borderBottom: `2px solid ${BRD}` }}>
            {fmtAmt(total)}
          </td>
          <td style={{ borderBottom: `2px solid ${BRD}` }} />
          <td style={{ borderBottom: `2px solid ${BRD}` }} />
        </tr>
        {!isLast && <tr><td colSpan={6} style={{ height: 8, background: SUR1 }} /></tr>}
      </React.Fragment>
    );
  };

  // ── Right subtotal ────────────────────────────────────────────────────────
  const renderRightSub = (g: { caseRef: string; exps: ExpectationRecord[] }, isLast: boolean) => {
    const anyActive = g.exps.some(e => activeExpIds.has(e.CommissionExpectationId) || matchedExpIds.has(e.CommissionExpectationId));
    const expTotal = g.exps.reduce((s, e) => s + (parseFloat(e.ExpectedAmount || "0") || 0), 0);
    const actTotal = g.exps.reduce((s, e) => s + (parseFloat(actualAmounts[e.CommissionExpectationId] ?? e.ExpectedAmount ?? "0") || 0), 0);
    return (
      <React.Fragment key={`rs-${g.caseRef}`}>
        <tr style={{ ...subStyle(g.caseRef, anyActive), cursor: "pointer" }}
          onMouseEnter={() => setHoveredRef(g.caseRef)} onMouseLeave={() => setHoveredRef(null)}
          onClick={() => toggleRightGroup(g.caseRef, g.exps)}>
          <td style={{ padding: "5px 12px", borderBottom: `2px solid ${BRD}` }} />
          <td style={{ padding: "5px 12px", fontSize: 11.5, color: T3, fontStyle: "italic", borderBottom: `2px solid ${BRD}` }}>Subtotal</td>
          <td style={{ padding: "5px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: T1, borderBottom: `2px solid ${BRD}` }}>{fmtAmt(expTotal.toFixed(2))}</td>
          <td style={{ padding: "5px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: Math.abs(actTotal - expTotal) > 0.001 ? ORA : T1, borderBottom: `2px solid ${BRD}` }}>{fmtAmt(actTotal.toFixed(2))}</td>
          <td colSpan={3} style={{ borderBottom: `2px solid ${BRD}` }} />
        </tr>
        {!isLast && <tr><td colSpan={7} style={{ height: 8, background: SUR1 }} /></tr>}
      </React.Fragment>
    );
  };

  const canProceed = matches.length > 0;
  const unmatched = activeDetailRows.filter(r => !matchedPaymentIds.has(r.CommissionStatementDetailId));
  const allPaysSel = unmatched.length > 0 && unmatched.every(r => activePaymentIds.has(r.CommissionStatementDetailId));
  const enabledExps = visibleExpectations.filter(e => activeCaseRefs.has(e.CaseRef) && !matchedExpIds.has(e.CommissionExpectationId));
  const allExpsSel = enabledExps.length > 0 && enabledExps.every(e => activeExpIds.has(e.CommissionExpectationId));

  const leftVirtual = useVirtualRows(activeDetailRows, viewMode === "individual");
  const rightVirtual = useVirtualRows(visibleExpectations, viewMode === "individual");

  const PANEL_H = "calc(100vh - 420px)";
  const PANEL: React.CSSProperties = {
    flex: 1, minWidth: 0, border: `1px solid ${BRD}`, borderRadius: 6,
    overflow: "hidden", display: "flex", flexDirection: "column", background: "#fff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  };

  if (loadingDetail) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, gap: 10 }}>
        <Spinner size="small" /><span style={{ fontSize: 13, color: T2 }}>Loading payment records…</span>
      </div>
    );
  }

  return (
    <div>
      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <SplitIcon />
        <span style={{ fontSize: 15, fontWeight: 700, color: T1, flex: 1 }}>Payments &amp; Expectations Reconciliation</span>
        <div style={{ background: "rgba(255,200,0,0.18)", border: "1px solid rgba(200,150,0,0.3)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#7a4f00", fontWeight: 500 }}>
          Hover any row to cross-highlight
        </div>
        <ViewToggle view={viewMode} onChange={setViewMode} />
      </div>

      {/* Dual panels */}
      <div style={{ display: "flex", gap: 12 }}>

        {/* LEFT — Payment Records */}
        <div style={PANEL}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${BRD}`, background: SUR1 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T1 }}>Payment Records</span>
            <Badge type="blue">{activePaymentIds.size + matchedPaymentIds.size} / {activeDetailRows.length} marked</Badge>
            <span style={{ marginLeft: "auto", fontSize: 12, color: T2 }}>{fmtAmt(leftTotal.toFixed(2))} selected</span>
          </div>
          <div data-testid="payment-records-scroll" style={{ overflowY: "auto", overflowX: "auto", flex: 1, minHeight: 280, maxHeight: PANEL_H }} onScroll={leftVirtual.onScroll}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
              <thead>
                <tr>
                  <th style={TH}>
                    <input type="checkbox" checked={allPaysSel} onChange={selectAllPayments}
                      aria-label="Select all payments"
                      style={{ accentColor: NAVY, cursor: "pointer" }} />
                  </th>
                  <th style={TH_R}>Row</th>
                  <th style={TH}>Case Reference</th>
                  <th style={TH_R}>Amount</th>
                  <th style={TH}>Client (file)</th>
                  <th style={TH} />
                </tr>
              </thead>
              <tbody>
                {activeDetailRows.length === 0 ? (
                  error.detail ? (
                    <tr><td colSpan={6} style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#ffe6e6", border: `1px solid ${RED}`, borderRadius: 5, fontSize: 12, color: RED }}>
                        <span style={{ flex: 1 }}>Failed to load payment records. Please try again.</span>
                        <button onClick={() => statementId && dispatch(fetchStatementDetail(statementId))}
                          style={{ background: RED, color: "#fff", border: "none", borderRadius: 4, fontSize: 11.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>Retry</button>
                      </div>
                    </td></tr>
                  ) : (
                    <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: T3, fontSize: 12 }}>No payment records.</td></tr>
                  )
                ) : viewMode === "individual" ? (
                  <>
                    {leftVirtual.topPad > 0 && <tr><td colSpan={6} style={{ height: leftVirtual.topPad, padding: 0 }} /></tr>}
                    {leftVirtual.visibleItems.map((r, i) => renderLeftRow(r, i))}
                    {leftVirtual.bottomPad > 0 && <tr><td colSpan={6} style={{ height: leftVirtual.bottomPad, padding: 0 }} /></tr>}
                  </>
                ) : (
                  leftGroups.map((g, gi) => (
                    <React.Fragment key={`lg-${g.caseRef}`}>
                      {g.rows.map((r, i) => renderLeftRow(r, i))}
                      {renderLeftSub(g, gi === leftGroups.length - 1)}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
            <div ref={leftSentinelRef} style={{ height: 1 }} />
            {loadingMoreDetail && <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}><Spinner size="tiny" /></div>}
          </div>
        </div>

        {/* RIGHT — Expectations */}
        <div style={PANEL}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${BRD}`, background: SUR1 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T1 }}>Expectations</span>
            {/* Denominator is visibleExpectations (Case Ref has a matching payment in
                this file), not every loaded unreceipted expectation for the provider/date
                range -- "Select All" can never exceed this, so the badge shouldn't imply
                otherwise. The excluded count below explains the gap instead of hiding it. */}
            <Badge type="green">{activeExpIds.size + matchedExpIds.size} / {visibleExpectations.length} selected</Badge>
            <span style={{ marginLeft: "auto", fontSize: 12, color: T2 }}>{fmtAmt(selectedExpAmt.toFixed(2))} selected</span>
          </div>
          <div data-testid="expectations-scroll" style={{ overflowY: "auto", overflowX: "auto", flex: 1, minHeight: 280, maxHeight: PANEL_H }} onScroll={rightVirtual.onScroll}>
            {loading.expectations ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, gap: 8 }}>
                <Spinner size="tiny" /><span style={{ fontSize: 12, color: T2 }}>Loading expectations…</span>
              </div>
            ) : error.expectations ? (
              <div style={{ padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#ffe6e6", border: `1px solid ${RED}`, borderRadius: 5, fontSize: 12, color: RED }}>
                  <span style={{ flex: 1 }}>Failed to load expectations. Please try again.</span>
                  <button onClick={() => { expectationPageRef.current = 1; dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: 1 })); }}
                    style={{ background: RED, color: "#fff", border: "none", borderRadius: 4, fontSize: 11.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>Retry</button>
                </div>
              </div>
            ) : activeCaseRefs.size === 0 && matchedCaseRefs.size === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160, padding: "20px 24px", textAlign: "center" }}>
                <span style={{ fontSize: 12.5, color: T3, maxWidth: 300 }}>
                  Select a Payment record on the left to list its matching Expectation records here.
                </span>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 580 }}>
                <thead>
                  <tr>
                    <th style={TH}>
                      <input type="checkbox" checked={allExpsSel} onChange={selectAllExpectations}
                        aria-label="Select all expectations"
                        style={{ accentColor: NAVY, cursor: "pointer" }} />
                    </th>
                    <th style={TH}>Case Reference</th>
                    <th style={TH_R}>Expected Amt</th>
                    <th style={TH_R}>Actual Amt</th>
                    <th style={TH}>Due Date</th>
                    <th style={TH}>Client (DB)</th>
                    <th style={TH} />
                  </tr>
                </thead>
                <tbody>
                  {visibleExpectations.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: T3, fontSize: 12 }}>No expectations found for the selected case references.</td></tr>
                  ) : viewMode === "individual" ? (
                    <>
                      {rightVirtual.topPad > 0 && <tr><td colSpan={7} style={{ height: rightVirtual.topPad, padding: 0 }} /></tr>}
                      {rightVirtual.visibleItems.map(e => renderRightRow(e))}
                      {rightVirtual.bottomPad > 0 && <tr><td colSpan={7} style={{ height: rightVirtual.bottomPad, padding: 0 }} /></tr>}
                    </>
                  ) : (
                    rightGroups.map((g, gi) => (
                      <React.Fragment key={`rg-${g.caseRef}`}>
                        {g.exps.map(e => renderRightRow(e))}
                        {renderRightSub(g, gi === rightGroups.length - 1)}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}
            <div ref={rightSentinelRef} style={{ height: 1 }} />
            {loadingMoreExpectations && <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}><Spinner size="tiny" /></div>}
          </div>
        </div>
      </div>

      {/* Match summary banner */}
      {matches.length > 0 && (
        <div style={{ marginTop: 10, border: `1px solid #aed6b8`, borderRadius: 5, padding: "8px 14px", background: GRNL, fontSize: 12.5, color: GRN }}>
          <strong>
            {matches.length} match{matches.length !== 1 ? "es" : ""} confirmed ·{" "}
            {fmtAmt(matches.reduce((s: number, m: MatchRecord) => s + m.matchedAmount, 0).toFixed(2))} total
          </strong>
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BRD}` }}>
        <button onClick={() => dispatch(setCurrentStep(2))}
          style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: 5, fontSize: 12.5, fontWeight: 500, padding: "0 14px", height: 32, cursor: "pointer" }}>
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={() => canProceed && dispatch(setCurrentStep(4))} disabled={!canProceed}
          style={{ background: canProceed ? NAVY : "#c8cdd5", color: "#fff", border: "none", borderRadius: 5, fontSize: 12.5, fontWeight: 500, padding: "0 18px", height: 32, cursor: canProceed ? "pointer" : "not-allowed" }}>
          Review &amp; Apply →
        </button>
      </div>

      {/* Detail modal */}
      {detailModalExp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setDetailModalExp(null)}>
          <div style={{ background: "#fff", borderRadius: 8, padding: "20px 24px", minWidth: 320, maxWidth: 440, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T1 }}>Expectation Details</span>
              <button onClick={() => setDetailModalExp(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: T3, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 12.5 }}>
              {[
                ["Case Reference", <span style={{ fontWeight: 600, color: NAVY, fontFamily: "monospace" }}>{detailModalExp.CaseRef || "—"}</span>],
                ["Commission Type", <span style={{ color: T1 }}>{detailModalExp.CommissionType || "—"}</span>],
                ["Due Date", <span style={{ color: T1 }}>{fmtDate(detailModalExp.DueDate)}</span>],
                ["Expected Amount", <span style={{ fontWeight: 600, color: T1, fontFamily: "monospace" }}>{fmtAmt(detailModalExp.ExpectedAmount)}</span>],
                ...(detailModalExp.CommissionNote ? [["Note", <span style={{ color: T1, maxWidth: 220, textAlign: "right" as const }}>{detailModalExp.CommissionNote}</span>]] : []),
                ["Status", <StatusChip status={getStatus(detailModalExp)} />],
              ].map(([label, value], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <span style={{ color: T3, flexShrink: 0 }}>{label}</span>
                  {value}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Toast stack */}
      <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 2000, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", pointerEvents: "none" }}>
        {toasts.map(t => (
          <div key={t.id} style={{ padding: "8px 14px", borderRadius: 6, background: "#1e293b", color: "#fff", fontSize: 12.5, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", maxWidth: 340 }}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepValidateMatch;
